/**
 * BuriedVerse E01 "Anxiety" — FULL overlay composition (production 25221141).
 *
 * fps-SAFE (S(sec) reads useVideoConfig().fps) so the SAME comp is correct at:
 *   • buriedverse-e01-overlays      @16fps → the 720p 0.5x preview (baked logo)
 *   • buriedverse-e01-overlays-4k   @24fps → the Topaz 4K base (Apollo 16→24), logo via ffmpeg
 * This is the bible_decoder W01 "devotional route" — overlay_render_4k reads the devotional
 * record in productions.overlay_design_spec, then build_overlay_windows.py EXTRACTS every
 * `start: X, out: Y` literal below and factory_4k_full renders those clusters at --scale=3.
 * ⚠️ Keep the window times as NUMERIC LITERALS in the start:/out: fields — the extractor
 * regexes them out of this file; a computed/variable expression would be invisible to it.
 *
 * Anchors are REAL times from productions.word_timestamps_hindi (4005 words, ends 1489.09s).
 * Every line of copy is grounded in what the narration says at that moment. 15 events / ~24.8 min.
 * Logo is NOT in this comp on the 4K route (showLogo=false): the cluster render only covers the
 * overlay windows, so ffmpeg lays the diorama mark full-runtime from channel_config (70px@720p×3
 * = 210 @4K). Slots: BvFactTag (citation/place/author) + BvHero (word/myth/quote).
 */
import {
  AbsoluteFill,
  Img,
  Sequence,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
} from "remotion";
import { BvFactTag, BvHero } from "./BuriedVerseOverlayDemo";

export const E01_FPS = 16;
export const E01_DURATION_FRAMES = 23932; // matches the 16fps 720p base exactly
// 4K route: the Topaz base is 24fps (Apollo 16→24 interp), 1495.775s → 35899 frames.
export const E01_FPS_4K = 24;
export const E01_DURATION_FRAMES_4K = 35899;

// Persistent logo — fades in once, stays for the whole episode.
const BvLogo: React.FC = () => {
  const frame = useCurrentFrame();
  const fadeIn = interpolate(frame, [0, 36], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return (
    <Img
      src={staticFile("buried_verse_diorama_ko.png")}
      style={{ position: "absolute", top: 20, right: 20, width: 70, height: "auto", opacity: fadeIn * 0.85 }}
    />
  );
};

/** Sequence unmounts hard; this fades the last 14 frames so cards don't pop out. */
const FadeOut: React.FC<{ dur: number; children: React.ReactNode }> = ({ dur, children }) => {
  const frame = useCurrentFrame();
  const o = interpolate(frame, [dur - 14, dur], [1, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return <AbsoluteFill style={{ opacity: o }}>{children}</AbsoluteFill>;
};

// start/out = ABSOLUTE seconds (out = the end time). build_overlay_windows.py regexes every
// `start: X, out: Y` literal out of this file for the 4K cluster render — keep them numeric.
type Tag = { start: number; out: number; kicker: string; value: string };
type Hero = {
  start: number;
  out: number;
  variant: "word" | "myth" | "quote";
  kicker: string;
  word?: string;
  translit?: string;
  gloss?: string;
  headline?: string;
  attribution?: string;
};

// ── SLOT 1 · fact tags ──────────────────────────────────────────────────────
const TAGS: Tag[] = [
  // "the verb on the lips of Jesus in Matthew chapter six" (81.7s)
  { start: 81.7, out: 86.2, kicker: "Citation", value: "Matthew 6" },
  // "the apostle Paul uses in Philippians chapter four" (87.5s)
  { start: 92.5, out: 97.0, kicker: "Citation", value: "Philippians 4" },
  // "the short, blunt letter of James" (245.6s)
  { start: 245.6, out: 250.6, kicker: "Citation", value: "James 1" },
  // "Proverbs twelve, verse twenty-five, puts it as plainly as it can be" (339.1s)
  { start: 341.0, out: 345.5, kicker: "Citation", value: "Proverbs 12:25" },
  // "a city called Philippi... In the year forty-two before Christ" (399.9s)
  { start: 401.5, out: 406.5, kicker: "Place", value: "Philippi" },
  // "He has just stood alone on Mount Carmel" (656.2s)
  { start: 659.0, out: 664.0, kicker: "Place", value: "Mount Carmel" },
  // "Psalm seventy-seven, an Asaphic psalm" (991.8s)
  { start: 991.8, out: 996.3, kicker: "Author", value: "Asaph" },
  // "in an olive grove called Gethsemane" (1064.4s)
  { start: 1064.4, out: 1069.4, kicker: "Place", value: "Gethsemane" },
  // "Hold that loosely against Philippians four." (1315.8s)
  // ⚠️ Was "Philippians 4:6" — WRONG twice: the narration says only "Philippians four" (no verse),
  // and the idea under it here ("God's peace, keeping watch at the gate of you") is 4:7, not 4:6.
  // Never put a verse number on screen that the script didn't say.
  { start: 1315.8, out: 1320.3, kicker: "Citation", value: "Philippians 4" },
];

// ── SLOT 2 · hero cards ─────────────────────────────────────────────────────
const HEROES: Hero[] = [
  // "The Greek is merimnaō." (78.0s) — the episode's central word
  {
    start: 78.0,
    out: 85.0,
    variant: "word",
    kicker: "The word",
    word: "μεριμνάω",
    translit: "merimnaō",
    gloss: "To be anxious, to be weighed down by care.",
  },
  // "Merimnaō does not 'mean to be divided.'" (122.4s) — narration verbatim
  {
    start: 122.4,
    out: 128.9,
    variant: "myth",
    kicker: "The myth",
    headline: "Merimnaō does not mean “to be divided.”",
  },
  // "di, two, and psyche, soul. Two-souled. Double-minded." (251.4s)
  {
    start: 253.0,
    out: 260.0,
    variant: "word",
    kicker: "The word",
    word: "δίψυχος",
    translit: "dipsychos",
    gloss: "Two-souled. Double-minded.",
  },
  // "cast all your anxiety on him, because he cares for you" (862.6s)
  {
    start: 866.0,
    out: 872.5,
    variant: "quote",
    kicker: "The text",
    headline: "“Cast all your anxiety on him, because he cares for you.”",
    attribution: "1 Peter 5:7",
  },
  // Matthew says he "began to be sorrowful and troubled" (1069.7s) — fires WITH the
  // Gethsemane place tag above: the one deliberate two-slot moment.
  {
    start: 1069.7,
    out: 1076.2,
    variant: "quote",
    kicker: "The text",
    headline: "“Began to be sorrowful and troubled.”",
    attribution: "Matthew 26:37",
  },
  // "not the removal of the thing, but the strength to stand in front of it" (1143s)
  {
    start: 1143.0,
    out: 1150.0,
    variant: "myth",
    kicker: "The turn",
    headline: "Not the removal of the thing, but the strength to stand in front of it.",
  },
];

/**
 * showLogo — the 16fps 0.5x PREVIEW bakes the diorama mark in (public/ is local).
 * The 24fps 4K route sets showLogo=false: the logo is added by ffmpeg in the modal
 * composite() from channel_config (full-runtime), because the 4K path renders only
 * per-event CLUSTERS — a Remotion-baked logo would appear only during those windows.
 * fps is read from useVideoConfig so the SAME comp is correct at 16fps AND 24fps.
 */
export const BuriedVerseE01Overlays: React.FC<{ showLogo?: boolean }> = ({
  showLogo = true,
}) => {
  const { fps } = useVideoConfig();
  const S = (sec: number) => Math.round(sec * fps);
  return (
    <AbsoluteFill>
      {showLogo && <BvLogo />}
      {TAGS.map((t, i) => (
        <Sequence key={`tag-${i}`} from={S(t.start)} durationInFrames={S(t.out - t.start)}>
          <FadeOut dur={S(t.out - t.start)}>
            <BvFactTag kicker={t.kicker} value={t.value} />
          </FadeOut>
        </Sequence>
      ))}
      {HEROES.map((h, i) => (
        <Sequence key={`hero-${i}`} from={S(h.start)} durationInFrames={S(h.out - h.start)}>
          <FadeOut dur={S(h.out - h.start)}>
            <BvHero
              variant={h.variant}
              kicker={h.kicker}
              word={h.word}
              translit={h.translit}
              gloss={h.gloss}
              headline={h.headline}
              attribution={h.attribution}
            />
          </FadeOut>
        </Sequence>
      ))}
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
